import { DesignSize } from "../../../presentation/screens/order-create/models/CartItem";
import { CasingOption } from "../../../presentation/shared/functions/casing";
import { ApprovalGroup } from "./Approval";
import { BaseLogo } from "./BaseLogo";
import { Design } from "./Design";
import { Organization } from "./Organization";
import { Price } from "./Price";

export interface Order {
  id: number;
  subTotal: number;
  total: number;
  vat: number;
  latestDelivery: Date;
  createdAtUtc: Date;
  updatedAtUtc: Date;
  placedAtUtc: Date;
  orderNumber: string;
  price: number;
  currency: string;
  status: OrderStatus;
  lines: OrderLine[];
  heating: Heating[];
  logoPrices: LogoPrice[];
  additionalFees: AdditionalFee[];
  deliveryType: DeliveryType;
  packagingType: Packaging;
  orderComment: string;
  originOfDelivery: string;
  requisitionNumber: string;
  organization: Organization;
  approval: ApprovalGroup | null;
  orderDeliveryAddress?: OrderDeliveryAddress;
  deliveryBefore12PMwithGLS?: boolean;
  // Stripe prepayment (vendor orgs with credit limit 0): when the API diverts
  // an order to "Awaiting payment" it persists a Checkout link. Optional -
  // only diverted orders carry them.
  stripePaymentUrl?: string;
  stripePaymentStatus?: string;
}

export type OrderLineDeliveryType = "Storage" | "Delivery" | "Application";

export interface LogoPrice {
  adjustedPrice: number;
  logoId: number;
}

export interface Heating {
  product: HeatingProduct;
  quantity: number;
}

export interface HeatingProduct {
  additionalFee?: AdditionalFee;
  isGenerated: boolean;
  name: string;
  prices: Price[];
  sku: string;
}

export interface OrderLine {
  id: number;
  displayNo: string;
  type: string;
  price: number;
  unitPrice: number;
  adjustedPrice: number;
  adjustedUnitPrice: number;
  quantity: number;
  title: string;
  displayImage?: string;
  toStorage: number;
  toDelivery: number;
  relatedTo: number;
  product: OrderLineProduct;
  deliveryType: OrderLineDeliveryType;
  additionalFees: AdditionalFee[];
  casing?: CasingOption;
  // When this line is one of several digit-split lines for a NumberLogo entry,
  // the original full number the customer typed ("12", "123"…). Used by the
  // typed-numbers popover to collapse per-digit lines back into one display row
  // per number. Plain single-digit numbers and non-number lines leave this
  // undefined.
  typedNumberValue?: string;
}

export enum OrderStatus {
  Unknown = "Unknown",
  Draft = "Draft",
  Pending = "Pending",
  AwaitingEntry = "Awaiting entry",
  AwaitingPayment = "Awaiting payment",
  Approved = "Approved",
  InProduction = "In Production",
  ProductionComplete = "Production Completed",
  OrderShipped = "Order Shipped",
  Cancelled = "Cancelled",
  Rejected = "Rejected",
  Processing = "Processing",
  Failed = "Failed",

  //NAV statuses
  // AwaitsRelease = "Awaits Release",
  // AcceptedAwaits = "Accepted Awaits",
  // ProductionRegistered = "Production Registered",
  // ProductionPlanned = "Production Planned",
  // ProductionFilmPrinting = "Production Film Printing",
  // ProductionFrameStarted = "Production Frame Started",
  // ProductionFrameFinished = "Production Frame Finished",
  // ProductionStarted = "Production Started",
  // ProductionProduced = "Production Produced",
  // ProductionCutted = "Production Cutted",
  // ProductionFinished = "Production Finished",
  // ProductionCancelled = "Production Cancelled",
  // Shipped = "Shipped",
}

export interface OrderLineProduct {
  productSku?: string;
  productMul?: string;
  productNo: string;
  productType: string;
  productName: string;
}

export interface OrderLineDesign extends OrderLine, OrderLineQuantity {
  design: Design;
  instances: OrderLineDesignInstance[];
}

export interface OrderLineLogo extends OrderLine, OrderLineQuantity {
  baseLogo: BaseLogo;
}

export interface OrderLineDesignInstance {
  label: string;
  quantity: number;
  size?: string;
  logos: OrderLineDesignInstanceLogo[];
}

export interface OrderLineDesignInstanceLogo {
  value: string | number;
  isNumeric: boolean;
  logoId: number;
}

export interface AdditionalFee {
  itemNo: string;
  name: string;
  quantity: number;
  price: number;
  prices: Price[];
  unitPrice: number;
}

export interface OrderDeliveryPreferences {
  company: string;
  zipCode: string;
  email: string;
  contactPerson: string;
  city: string;
  contactPhone: string;
  address: string;
  // Optional "Address line 2" (flag REACT_APP_SHIPTO_ADDRESS2_ENABLED, paired
  // with API flag OrderShipToAddress2Enabled). "" is fine — the API treats the
  // field as optional and caps it at 50 chars for NAV.
  address2?: string;
  country: string;
  emailtrackAndTrace: string;

  latestDeliveryDate: Date;
  deliveryBefore12PMwithGLS: boolean;

  deliveryType: DeliveryType;
  packaging: Packaging;

  orderComment: string;
  originOfDelivery: string;
  requisitionNumber: string;
  shippingService?: string;
}

export enum DeliveryType {
  Standard = "STANDARD",
  Express24 = "EXPRESS 24",
  Express48 = "EXPRESS 48",
  Express72 = "EXPRESS 72",
}

export enum Packaging {
  NoPackaging = "NoPackaging",
  StandardBagPackaging = "StandardBagPackaging",
  SpecificPackaging = "SpecificPackaging",
}

export interface OrderLineQuantity {
  toStorage: number;
  toDelivery: number;
}

export interface OrderDeliveryAddress {
  id: number;
  orderId: number;
  name: string;
  address: string;
  address2: string;
  zip: string;
  city: string;
  country: string;
  contact: string;
  email: string;
  phone: string;
  isDefault: boolean;
}
